/**
 * Confirmation contract for the dedicated "Delete account" page.
 *
 * To proceed with the (irreversible-after-grace-period) deletion the user must
 * re-type the EXACT email of the account being deleted into a confirmation box.
 * This is a deliberate friction step against ACCIDENTAL deletion and against
 * confusing "Delete account" with "Sign out". It is NOT a security boundary —
 * the real authorization is the session cookie the backend checks on
 * `requestAccountDeletion`; this only gates the destructive button in the UI.
 *
 * Matching is normalized with trim + lowercase (case-insensitive):
 *   - Email domains are case-insensitive by spec, and every mainstream provider
 *     also treats the local-part case-insensitively, so requiring exact case
 *     would only produce false negatives that frustrate a legitimate user.
 *   - A leading/trailing space from autofill or copy-paste should not block a
 *     correct confirmation.
 * The trade-off (someone could type a different-cased variant) is irrelevant:
 * this is an anti-mistake gate, not authentication.
 */
export function normalizeEmailForConfirmation(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Returns `true` only when `input` matches the account email under the
 * normalization rules above. An empty/absent account email never matches, so a
 * missing session can never accidentally enable the destructive button.
 */
export function emailMatchesAccount(
  input: string,
  accountEmail: string | null | undefined,
): boolean {
  if (!accountEmail) {
    return false;
  }
  const normalizedAccount = normalizeEmailForConfirmation(accountEmail);
  if (normalizedAccount === "") {
    return false;
  }
  return normalizeEmailForConfirmation(input) === normalizedAccount;
}

/** Suffix of the dedicated deletion route. */
export const DELETE_ACCOUNT_PATH_SUFFIX = "/profile/delete-account";

/** `true` when `pathname` still points at the dedicated deletion page. */
export function isDeleteAccountPath(pathname: string): boolean {
  return pathname.endsWith(DELETE_ACCOUNT_PATH_SUFFIX);
}

/**
 * Guard for the "a guest has no account to delete" redirect.
 *
 * A guest must be bounced off the deletion page to the profile — but the
 * redirect has to be issued AT MOST ONCE, and the `pathname` term is what makes
 * that true. It is not a stylistic guard: it closes a self-feeding reactive
 * loop at its source.
 *
 * Why: an effect shaped like «if visitor → navigate» READS the pathname and then
 * CHANGES the pathname: the navigation re-triggers the very effect that issued
 * it, and `isVisitor()` is still `true` at the destination (a guest stays a
 * guest), so the effect navigates a second time — or, without a fixed point at
 * the destination, keeps navigating forever and hangs the tab (measured in a
 * sibling app: the page stayed blank, `load` never fired, the main thread stuck
 * in `runUpdates → completeUpdates → runUpdates`).
 *
 * (Under the `/u/<n>` URL scheme this loop was worse: the per-tab account was
 * parsed OUT of the pathname, so `isVisitor()` itself was pathname-reactive.
 * With the account in `sessionStorage` that coupling is gone, but the one-shot
 * anchor below is still what makes the redirect fire exactly once.)
 *
 * Anchoring the redirect to «we are STILL on the deletion page» closes it at
 * the source: once the first navigation lands on `/profile`, the condition is
 * false and no second navigation is ever issued.
 *
 * `isPending` is honoured first so an in-flight session verification never
 * expels a real signed-in user mid-boot.
 */
export function shouldRedirectGuestAwayFromDeletion(input: {
  isPending: boolean;
  isVisitor: boolean;
  pathname: string;
}): boolean {
  if (input.isPending) {
    return false;
  }
  if (!input.isVisitor) {
    return false;
  }
  return isDeleteAccountPath(input.pathname);
}
